import { SelectOption } from '@app/app/_components/Input/Select/SelectOption';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { ReactNode, useCallback, useRef, KeyboardEvent } from 'react';
import { twJoin } from 'tailwind-merge';

interface Props {
    id?: string;
    option: SelectOption;
    selected?: boolean;
    highlighted?: boolean;
    focusable?: boolean;
    onClick?: (value: SelectOption) => void;
    children: ReactNode;
}

export function SelectItem({ id, option, children, selected, highlighted, focusable, onClick }: Props) {
    const itemRef = useRef<HTMLLIElement>(null);

    const handleClick = useCallback(() => {
        onClick?.(option);
    }, [onClick, option]);

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLLIElement>) => {
        if (e.code === 'Enter') {
            e.currentTarget.click();

            e.preventDefault();
            e.stopPropagation();
        }
    }, []);

    useChangeEffect(() => {
        if (!highlighted) {
            return;
        }

        if (focusable) {
            itemRef.current?.focus();
        }

        itemRef.current?.scrollIntoView({ block: 'nearest' });
    }, highlighted);

    return (
        <li
            ref={itemRef}
            id={id}
            className={twJoin(
                'inline-flex cursor-pointer rounded px-3 py-2.5 text-sm md:py-1.25',
                selected && 'bg-primary-200',
                !selected && highlighted && 'bg-neutral-100',
                !selected && !highlighted && 'hover:bg-neutral-100',
            )}
            role="option"
            tabIndex={focusable ? (highlighted ? 0 : -1) : -1}
            onKeyDown={handleKeyDown}
            onClick={handleClick}
            aria-label={option.label}
            aria-selected={selected ?? false}>
            {children}
        </li>
    );
}
